/**
 * useOfflineQueue — Local buffer for actions taken while WebSocket is offline.
 *
 * How it works:
 *  1. When isOnline=false, enqueue(action) saves to localStorage instead of API
 *  2. When isOnline flips to true, flush() replays all queued actions
 *  3. Failed flushes stay in the queue and retry on next reconnect
 *
 * Usage:
 *   const { enqueue, queueSize } = useOfflineQueue(
 *     isOnline,
 *     async (action) => await api.completeTask(action.task_id, action.completed_by)
 *   );
 *
 *   // In a task completion handler:
 *   if (isOnline) {
 *     await api.completeTask(taskId, staffName);
 *   } else {
 *     enqueue({ task_id: taskId, completed_by: staffName, action: 'complete_task' });
 *     toast.info("Saved offline — will sync when connected");
 *   }
 */

import { useState, useEffect, useRef } from "react";

const QUEUE_KEY = "argus_offline_task_queue";

export function useOfflineQueue(isOnline, syncFn) {
  const [queueSize, setQueueSize] = useState(0);
  const syncRef = useRef(syncFn);
  syncRef.current = syncFn;

  // Init queue size from localStorage on mount
  useEffect(() => {
    try {
      const q = JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
      setQueueSize(q.length);
    } catch {
      localStorage.removeItem(QUEUE_KEY);
    }
  }, []);

  // Flush when coming back online
  useEffect(() => {
    if (isOnline) {
      flush();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  const enqueue = (action) => {
    try {
      const q = JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
      q.push({ ...action, queued_at: Date.now() });
      localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
      setQueueSize(q.length);
    } catch (e) {
      console.warn("[OfflineQueue] enqueue failed:", e);
    }
  };

  const flush = async () => {
    let q;
    try {
      q = JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
    } catch {
      localStorage.removeItem(QUEUE_KEY);
      return;
    }

    if (!q.length) return;

    const failed = [];
    for (const action of q) {
      try {
        await syncRef.current(action);
      } catch {
        failed.push(action); // keep failed actions for next retry
      }
    }

    localStorage.setItem(QUEUE_KEY, JSON.stringify(failed));
    setQueueSize(failed.length);
  };

  const clearQueue = () => {
    localStorage.removeItem(QUEUE_KEY);
    setQueueSize(0);
  };

  return { enqueue, queueSize, clearQueue };
}
